import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { colocService, setSelectedColoc } from "../services/coloc.service";
import { libelleSitePublic } from "../utils/publicUrl";
import "../styles/ColocDetailsPage.css";

function slugify(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[-\s]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const COUNTRIES = [
  { code: "FR", label: "France" },
  { code: "BE", label: "Belgique" },
  { code: "CH", label: "Suisse" },
  { code: "LU", label: "Luxembourg" },
];

const CreateColocPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("FR");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(slugify(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const coloc = await colocService.createColoc({
        name,
        address,
        zipCode,
        city,
        countryCode,
        slug: slug || undefined,
      });
      setSelectedColoc(coloc);
      window.location.href = "/details";
    } catch (err: unknown) {
      if (err && typeof err === "object" && "response" in err) {
        const response = (err as { response: { data?: { detail?: string } } }).response;
        setError(response.data?.detail || "Erreur lors de la création");
      } else {
        setError("Erreur lors de la création de la colocation");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="coloc-details-page">
      <h1>Créer une colocation</h1>

      {error && <div className="message error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <section className="form-section">
          <h2>Informations générales</h2>
          <div className="form-group">
            <label htmlFor="name">Nom de la colocation *</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="Ex: Ma Colocation Lille"
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">Slug (URL) *</label>
            <input
              type="text"
              id="slug"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              required
              placeholder="Ex: ma-colocation-lille"
            />
            {slug && (
              <small className="form-help" style={{ display: "block", marginTop: "4px", color: "#666" }}>
                Aperçu : <strong>{libelleSitePublic(slug)}</strong>
              </small>
            )}
            <small className="form-help" style={{ display: "block", marginTop: "4px", color: "#b45309" }}>
              Le slug ne pourra plus être modifié après la création. L'URL publique de votre colocation sera disponible une fois celle-ci créée.
            </small>
          </div>
        </section>

        <section className="form-section">
          <h2>Adresse</h2>
          <div className="form-group">
            <label htmlFor="address">Adresse *</label>
            <input
              type="text"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              placeholder="Ex: 12 rue de la Paix"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="zipCode">Code postal *</label>
              <input
                type="text"
                id="zipCode"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                required
                placeholder="Ex: 59000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">Ville *</label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                placeholder="Ex: Lille"
              />
            </div>

            <div className="form-group">
              <label htmlFor="countryCode">Pays *</label>
              <select
                id="countryCode"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                required
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <div className="form-actions">
          <button type="submit" className="save-button" disabled={isSaving}>
            {isSaving ? "Création en cours..." : "Créer la colocation"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateColocPage;
